import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../../lib/analytics";
import { WRS_SECTION } from "./sections.js";

/**
 * Questions about wedding RSVPs.
 *
 * A plain definition list, always expanded — same reasoning as the three
 * sibling FAQs: every answer is short enough to read in place, and these are
 * the page's most self-contained factual passages, which is exactly what an
 * assistant quotes. Putting them behind an accordion would hide them from a
 * reader and gain nothing from a crawler.
 *
 * Every answer is checked against the implementation. The five that are easiest
 * to get wrong, and what the code actually says:
 *
 *  - `change` — the submit handler 409s on an already-submitted guest
 *    (`RSVP_ALREADY_SUBMITTED`), and the loader returns `alreadySubmitted: true`
 *    with a read-only recap. A guest can re-open their link and SEE their
 *    answer; they cannot rewrite it. The couple can, via
 *    `PUT /guests/:guestId/attendance`.
 *  - `dietary` — `rsvpSubmissionSchema` has no dietary field of any kind
 *    (api/validators.mjs). `dietaryRequirements` lives on the party member and
 *    is written only through the couple's `PUT /guests/:guestId/party`, then
 *    carried into the seating pool. "Kept by you" is literal.
 *  - `custom` — there is no question model anywhere. The RSVP is one fixed
 *    matrix of member × section with three statuses (`STATUS_VALUES` in
 *    invite/src/components/rsvp/rsvpConstants.js). What IS configurable is the
 *    sections themselves and their `isRSVPRequired` flag.
 *  - `approve` — a guest-added member is written `reviewStatus: "pendingReview"`
 *    and is excluded from every count until approved; rejecting one pulls their
 *    responses out of the attendance matrix as well.
 *  - `language` — `guest.preferredLanguage` drives the invitation EMAIL
 *    (`sendGuestInvitation`, api/services/email.service.mjs). The personal link
 *    itself carries no `?lang=`, so the wording is careful to say the email is
 *    sent in the guest's language and that the invitation is written in both —
 *    never that the page opens pre-switched.
 *
 * `website`, `dietary` and `custom` are the three disambiguation answers: the
 * only places on this page where "wedding website builder", "dietary
 * preferences" and "custom RSVP questions" are allowed to appear at all. The
 * forbidden-claims test pins those exceptions to these keys.
 */
const FAQ_KEYS = [
  "what",
  "howWorks",
  "insideInvitation",
  "ownLink",
  "perMoment",
  "companions",
  "approve",
  "after",
  "guestList",
  "language",
  "change",
  "dietary",
  "custom",
  "plan",
  "website",
];

const RsvpFaq = () => {
  const { t } = useTranslation();
  const analyticsContext = useAnalyticsContext();

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WRS_SECTION.FAQ, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-section-header">
          <h2 className="heading-50">{t("weddingRsvp.faq.heading")}</h2>
        </div>

        <dl className="wps-faq">
          {FAQ_KEYS.map((key) => (
            <div key={key} className="wps-faq-item">
              <dt className="made-22 wps-faq-q">
                {t(`weddingRsvp.faq.items.${key}.q`)}
              </dt>
              <dd className="wps-faq-a body-15">
                {t(`weddingRsvp.faq.items.${key}.a`)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default RsvpFaq;
